import React, { useState } from 'react';
import styles from './SlippageSettings.module.css';

interface SlippageSettingsProps {
  slippageBps: number;
  deadlineMinutes: number;
  onSlippageChange: (bps: number) => void;
  onDeadlineChange: (minutes: number) => void;
}

const PRESETS = [10, 50, 100]; // bps: 0.1%, 0.5%, 1%

export function SlippageSettings({
  slippageBps,
  deadlineMinutes,
  onSlippageChange,
  onDeadlineChange,
}: SlippageSettingsProps) {
  const [open, setOpen] = useState(false);
  const [customSlippage, setCustomSlippage] = useState('');
  const [customDeadline, setCustomDeadline] = useState('');

  const slippagePct = (slippageBps / 100).toFixed(slippageBps % 100 === 0 ? 1 : 2);
  const isHighSlippage = slippageBps > 100;

  const handleCustomSlippage = (val: string) => {
    setCustomSlippage(val);
    const pct = parseFloat(val);
    if (!isNaN(pct) && pct > 0 && pct <= 50) {
      onSlippageChange(Math.round(pct * 100));
    }
  };

  const handleCustomDeadline = (val: string) => {
    setCustomDeadline(val);
    const mins = parseInt(val, 10);
    if (!isNaN(mins) && mins > 0 && mins <= 4320) {
      onDeadlineChange(mins);
    }
  };

  return (
    <div className={styles.wrap}>
      <button className={styles.trigger} type="button" onClick={() => setOpen(o => !o)}>
        <GearIcon />
        <span className={styles.triggerLabel}>
          Slippage: <span className={isHighSlippage ? styles.high : styles.normal}>{slippagePct}%</span>
        </span>
        <span className={styles.triggerLabel}>· {deadlineMinutes}m</span>
        <ChevronIcon open={open} />
      </button>

      {open && (
        <div className={styles.panel}>
          <div className={styles.section}>
            <span className={styles.sectionTitle}>Slippage Tolerance</span>
            {isHighSlippage && (
              <span className={styles.warning}>⚠ High slippage — use with care</span>
            )}
            <div className={styles.presetRow}>
              {PRESETS.map(bps => (
                <button
                  key={bps}
                  type="button"
                  className={`${styles.preset} ${slippageBps === bps && !customSlippage ? styles.presetActive : ''}`}
                  onClick={() => { onSlippageChange(bps); setCustomSlippage(''); }}
                >
                  {(bps / 100).toFixed(1)}%
                </button>
              ))}
              <div className={styles.customWrap}>
                <input
                  type="text"
                  inputMode="decimal"
                  className={styles.customInput}
                  placeholder="Custom"
                  value={customSlippage}
                  onChange={e => handleCustomSlippage(e.target.value)}
                />
                {customSlippage && <span className={styles.pctSuffix}>%</span>}
              </div>
            </div>
          </div>

          <div className={styles.divider} />

          <div className={styles.section}>
            <span className={styles.sectionTitle}>Transaction Deadline</span>
            <div className={styles.deadlineRow}>
              <input
                type="text"
                inputMode="numeric"
                className={styles.deadlineInput}
                value={customDeadline || deadlineMinutes}
                onChange={e => handleCustomDeadline(e.target.value)}
              />
              <span className={styles.deadlineLabel}>minutes</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GearIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
      <path d="M7.5 9.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" stroke="currentColor" strokeWidth="1.2" />
      <path d="M12.2 5.5l.8-1.4-1.4-1.4-1.4.8a4.5 4.5 0 0 0-1.2-.5L8.5 1.5h-2l-.5 1.5a4.5 4.5 0 0 0-1.2.5L3.4 2.7 2 4.1l.8 1.4a4.5 4.5 0 0 0-.3 1.5L1 7.5l1.5 1 .3 1.5-.8 1.4 1.4 1.4 1.4-.8c.4.2.8.4 1.2.5l.5 1.5h2l.5-1.5c.4-.1.8-.3 1.2-.5l1.4.8 1.4-1.4-.8-1.4c.2-.5.3-1 .3-1.5l1.5-.5-1.5-1a4.5 4.5 0 0 0-.3-1.5Z" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="12" height="12" viewBox="0 0 12 12" fill="none"
      style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
    >
      <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
